import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges, ViewChild } from "@angular/core";
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { BehaviorSubject } from "rxjs";
import { OopsComponentFormBase } from "src/app/core/base/oops-component-form-base";
import { AttributeChoiceModel, DomainAttributeModel } from "src/app/core/models/reference-model/reference-general-model";
import { duplicateGroupValidation } from "src/app/core/validators";
import { Select2Option } from "src/app/modules/pricing/rules/price-rule-detail/attribute-and-rule/shared/attribute-and-rule/views/select2.option";
import { Select2Data } from "src/app/shared/ui/forms/inputs/oops-select2";
import { AttributeChoiceMultipleOption, AttributeChoiceSingleOption } from "./shared/select2.configuration";
import { AttributeRowView } from "./shared/attribute-row-view";
import { FocusingDirective } from "src/app/shared/ui/forms/inputs/focusing.directive";
import { cloneDeep } from "lodash";
@Component({
    selector: 'op-document-distribution-attribute',
    templateUrl: './document-distribution-attribute.component.html'
})
export class DocumentDistributionAttributeComponent extends OopsComponentFormBase {
    public readonly title = "Attribute";
    private readonly ATTRIBUTE_TYPE = "attributeType";

    public domainAttributes$ = new BehaviorSubject<DomainAttributeModel[]>(null);
    public attributeChoices$ = new BehaviorSubject<Array<Select2Data[]>>(null);
    public panelCollapseFlag$ = new BehaviorSubject<boolean>(true);
    public attributeType$ = new BehaviorSubject<Select2Data[]>(null);
    public attributeChoiceOption$ = new BehaviorSubject<any[]>(null);
    public focusing: boolean = false;
    public attributeChoiceOptions: any[];
    public attributeChoices = new Array<Select2Data[]>();

    public select2AttributeTypeOption = new Select2Option("<Select>");
    public select2AttributeTypeDisableOption = new Select2Option("<Select>");

    public atributeSelectLists: Select2Data[][] = [];
    private firstSetupAttribute = true;
    private _domainAttributes: DomainAttributeModel[];

    @Input() set domainAttributes(value: DomainAttributeModel[]) {
        if (value?.length) {
            this._domainAttributes = value;
            this.assignDataToReferenceModel();
            this.assignOptionListToSelect2();
            if (this.firstSetupAttribute == true) {
                this.firstSetupAttribute = false;
                this.setupDefaultAttribute();
            }
        }
    };
    @Input() newDocumentDistribution: boolean = false;
    @ViewChild(FocusingDirective) focusingDirective: FocusingDirective;

    
   
    get allowToggle(): boolean {
        return this.rows?.length > 0;
    }

    get rows(): UntypedFormArray {
        return this.formGroup?.get("rows") as UntypedFormArray;
    }

    get isReadonly(): boolean {
        return false;
    }

    get selectedArray(): string[] {
        return this.rows?.value?.map(item => item['attributeType']);
    }

    constructor(private fb: UntypedFormBuilder,
        private changeDetectorRef: ChangeDetectorRef) {
        super(fb);
    }

    public initForm() {
        this.formGroup = new UntypedFormGroup({
            rows: new UntypedFormArray([], duplicateGroupValidation),
        });

        this.togglePanelCollapseStatus(true);
    }

    private assignDataToReferenceModel() {
        this.domainAttributes$.next(this._domainAttributes);
        this.attributeType$.next(this._domainAttributes.map(item => new Select2Data(item.attributeTypeCode, item.attributeTypeName)));
    }

    private assignOptionListToSelect2() {
        this.populateChoices(this._domainAttributes);
        this.populateChoiceOptions(this._domainAttributes);
    }   

    private populateChoiceOptions(res: DomainAttributeModel[]) {
        let optionArr = new Array<any>();
        for (let r of res) {
            optionArr[r.attributeTypeCode] = r.multipleChoiceFlag
                ? AttributeChoiceMultipleOption
                : AttributeChoiceSingleOption;
        }
        this.attributeChoiceOption$.next(optionArr);
        this.attributeChoiceOptions = optionArr;
    }

    private setupDefaultAttribute() {
        if (this.newDocumentDistribution) {
            this.addRowFollowDomainRequiredFlag();
            this.addShowOnNewAttribute();
        }
    }

    private addShowOnNewAttribute() {
        let showOnNewAttributes = this.domainAttributes$.value?.filter(it => (it.showOnNewFlag && !it.requiredFlag) || (it.showOnNewFlag && it.requiredFlag)) ?? [];
        for (let showOnNewAttribute of showOnNewAttributes) {
            this.panelCollapseFlag$.next(false);
            let formGroup = this.createNewFormGroup();
            this.onAttributeTypeChange(showOnNewAttribute.attributeTypeCode, formGroup);

            this.rows.push(formGroup);
            this.toggleCollapseFollowNumberOfRows();
        }
        this.setAttributeLists();
    }

    private addRowFollowDomainRequiredFlag() {
        if (this.rows.controls?.length) {
            return;
        }

        let requiredAttributes = this.domainAttributes$.value?.filter(it => (it.requiredFlag && !it.showOnNewFlag)) ?? [];
        for (let requiredAttribute of requiredAttributes) {
            this.panelCollapseFlag$.next(false);
            let formGroup = this.createNewFormGroup();
            formGroup.controls['requiredFlag'].setValue(true);
            this.onAttributeTypeChange(requiredAttribute.attributeTypeCode, formGroup);

            this.rows.push(formGroup);
            this.toggleCollapseFollowNumberOfRows();
        }
        this.setAttributeLists();
    }

    private populateChoices(res: DomainAttributeModel[]) {
        let choicesArr = new Array<Select2Data[]>();
        for (let r of res) {
            let choices = r.attributeChoices.map(c => new Select2Data(c.attributeChoiceId, c.attributeChoiceName));
            choicesArr[r.attributeTypeCode] = choices;
        }
        this.attributeChoices$.next(choicesArr);
        this.attributeChoices = choicesArr;
    }

    togglePanelCollapseStatus(isCollapse: boolean) {
        if (!isCollapse && !this.rows?.length) {
            return;
        }
        this.panelCollapseFlag$.next(isCollapse);
    }

    add() {
        if (this.someEmptyEntriesFound()) {
            return;
        }

        this.panelCollapseFlag$.next(false);
        this.rows.push(this.createNewFormGroup());
        this.setAttributeListsForNewRow();
        this.toggleCollapseFollowNumberOfRows();
    }

    private createNewFormGroup(): UntypedFormGroup {
        return this.fb.group({
            attributeType: [null, [Validators.required]],
            dimensionUnitCode: [null],
            attributeData: [null],
            multipleChoiceFlag: [null],
            requiredFlag: [null],
            attributeChoice: [null],
            choiceValue: [null, [Validators.required]],
            maxValue: [null],
            minValue: [null],
            choiceOption:[null]
        });
    }

    private someEmptyEntriesFound(): boolean {
        return this.rows.controls.some((group: UntypedFormGroup) => {
            return Object.keys(group.controls).some(key =>
                group.controls[key].hasError('required')
            );
        });
    }

    public deleteRow(index: number) {
        this.rows.removeAt(index);
        this.toggleCollapseFollowNumberOfRows();
    }

    public toggleCollapseFollowNumberOfRows() {
        if (this.rows?.length == 0) {
            this.panelCollapseFlag$.next(true);
        } else {
            this.panelCollapseFlag$.next(false);
        }
    }
    
    public checkHiddenDelete(row): boolean {
        if (!this.checkIsRequiredFlag(row.get('attributeType').value)) {
            return false;
        }
        return true;
    }

    private checkIsRequiredFlag(attributeTypeCode: string) {
        if (attributeTypeCode && this.domainAttributes$.value) {
            var filter = this.domainAttributes$.value.filter(x =>
                x.attributeTypeCode == attributeTypeCode && x.requiredFlag == true);
            if (filter?.length) {
                return true;
            }
        }
        return false;
    }

    public getSelect2Option(requireFlag: boolean): Select2Option {
        if(requireFlag) {
            return cloneDeep(this.select2AttributeTypeDisableOption);
        }
        else {
            return cloneDeep(this.select2AttributeTypeOption);
        }
    }

    onAttributeTypeChange(type: any, row: any) {
        row.controls['attributeType'].setValue(type);
        if (!this.domainAttributes$.value?.length) {
            return;
        }
        let attribute = this.domainAttributes$.value?.filter(item => item.attributeTypeCode == type)[0];
        this.setupAttributeRow(row, attribute);
    }

    setupAttributeRow(row: UntypedFormGroup, attribute: DomainAttributeModel) {
        row.get('dimensionUnitCode').setValue(attribute.dimensionUnitCode);
        row.get('maxValue').setValue(attribute.maximumValue);
        row.get('minValue').setValue(attribute.minimumValue);
        row.get('requiredFlag').setValue(attribute.requiredFlag);

        if (attribute.dimensionUnitCode == 'CHOICE') {
            this.setupAttributeChoice(row, attribute);
        } else if (attribute.dimensionUnitCode == 'NUMBER') {
            this.setDefaultValue(row, attribute);
        }
    }

    setupAttributeChoice(row: UntypedFormGroup, attribute: DomainAttributeModel) {
        row.get('choiceOption').setValue(this.attributeChoiceOptions[attribute.attributeTypeCode]);
        row.get('attributeChoice').setValue(this.attributeChoices[attribute.attributeTypeCode]);
        row.get('multipleChoiceFlag').setValue(attribute.multipleChoiceFlag);
        let defaultChoice = this.getDefaultAttributeCode(attribute);
        let defaultChoicOnSelect2Data = this.getFirstAttributeChoice(attribute);
        if (defaultChoice?.length > 0) {
            this.setDefaultChoiceValue(row, attribute, defaultChoice);
        }
        else {
            this.setDefaultChoiceValueBySelect2Data(row, attribute, defaultChoicOnSelect2Data);
        }
    }

    setDefaultChoiceValueBySelect2Data(row: UntypedFormGroup, attribute: DomainAttributeModel, choices: Select2Data[]) {
        if (attribute.multipleChoiceFlag) {
            row.get('choiceValue').setValue(choices?.map(item => item.id));
        } else {
            row.get('choiceValue').setValue(choices[0]?.id);
        }
    }

    getDefaultAttributeCode(attribute: DomainAttributeModel)  {
        let choices = attribute.attributeChoices?.filter(choice => choice.defaultFlag == true);
        return choices?.length > 0 ? choices : null;
    }

    getFirstAttributeChoice(attribute: DomainAttributeModel) {
        let choice = [];
        if (this.attributeChoices[attribute.attributeTypeCode]?.length > 0) {
            choice.push(this.attributeChoices[attribute?.attributeTypeCode][0]);
        }
        return choice;
    }

    setDefaultChoiceValue(row: UntypedFormGroup, attribute: DomainAttributeModel, choices: AttributeChoiceModel[]) {
        if (attribute.multipleChoiceFlag) {
            row.get('choiceValue').setValue(choices?.map(item => item.attributeChoiceId));
        } else {
            row.get('choiceValue').setValue(choices[0]?.attributeChoiceId);
        }
    }

    setDefaultValue(row: UntypedFormGroup, attribute: DomainAttributeModel) {
        if (attribute.defaultValue) {
            row.get('choiceValue').setValue(attribute?.defaultValue);
        }
    } 

    setAttributeLists() {
        let attributeLists: Select2Data[][] = [];

        for (let row of this.rows.controls) {   
            if (this.checkIsRequiredFlag(row.get('attributeType').value)) {
                let test = this.attributeType$.value?.filter(x => x.id == row.get('attributeType').value).map(item => item);
                attributeLists.push(test);
            }  else {
                let test = this.attributeType$.value?.filter(x => this.selectedArray.includes(x.id) == false || x.id == row.get('attributeType').value).map(item => item);
                attributeLists.push(test);
            }
        }

        this.atributeSelectLists = (attributeLists);
    }

    setAttributeListsForNewRow() {
        this.atributeSelectLists.push(this.attributeType$.value.filter(attr => this.rows.controls.map(ctrl => ctrl.get('attributeType').value).includes(attr.id) == false))
    }
    
    dataChange(value: any, row: any) {
        row.get('choiceValue').setValue(value);
        if (row.value.dimensionUnitCode == 'NUMBER') {
            this.validateNumber(value, row);
        }
    }

    validateNumber(value: string, row: UntypedFormGroup) {
        if (value == undefined) {
            row.setErrors({outOfRange: true});
            return;
        }
        row.setErrors(null);
    }

    public getDocumentDistributionAttribute(): AttributeRowView[] {
        if (!this.rows.controls?.length) {
            return null;
        }
        let views = new Array();
        for (let i = 0; i < this.rows.controls.length; i++) {          
            let control = this.rows.controls[i] as UntypedFormGroup;
            if (!control.get('choiceValue').value && control.get('requiredFlag').value != true) {
                continue;
            }
            let attribCode = control.controls[this.ATTRIBUTE_TYPE].value;
            let view: AttributeRowView = {
                attributeChoiceIds: null,
                attributeTypeCode: attribCode,
                attributeTypeName: this.getAttributeName(attribCode),
                attributeValue: null,
                duplicated: false,
                isFreeTextChoice: false,
                sortSequence: i+1,
                isMultiChoices: false,
                attributeText: null,
                attributeDateTime: null
            };
            this.assignAttributeValue(control, view);

            views.push(view);
        }
        return views;
    }

    getAttributeName(attributeTypeCode: string) {
        return this.domainAttributes$.value.filter(item => item.attributeTypeCode == attributeTypeCode)[0].attributeTypeName;
    }

    private assignAttributeValue(row: UntypedFormGroup,view: AttributeRowView) {
        switch (row.value.dimensionUnitCode) {
            case 'NUMBER':
            case 'PERCENTAGE':
                view.attributeValue = row.value.choiceValue;
                break;
            case 'DATE':
            case 'TIME':
            case 'DATETIME':
                view.attributeDateTime = row.value.choiceValue;
                break;
            case 'TEXT':
                view.attributeText = row.value.choiceValue;
                break;
            case 'CHOICE':
                this.assignChoiceValue(row, view);
                break;
        }
    }

    assignChoiceValue(row: UntypedFormGroup,view: AttributeRowView) {
        if (Array.isArray(row.value.choiceValue)) {
            view.attributeChoiceIds = row.value.choiceValue;
        } else {
            view.attributeChoiceIds = [row.value.choiceValue];
        }
    }

    public fillModelToForm(views: AttributeRowView[]) {
        if (!this._domainAttributes?.length) {
            return;
        }

        if (views?.length) {
            this.rows?.clear();
            this.panelCollapseFlag$.next(false);
              
            for (let view of views) {
                let formGroup = this.createNewFormGroup();
                this.onAttributeTypeChange(view.attributeTypeCode, formGroup);
                
                let attribute = this._domainAttributes.filter(item => item.attributeTypeCode == view.attributeTypeCode)[0];
                this.addAttributeValue(formGroup, view, attribute);
                this.rows.push(formGroup);
            }
            this.setAttributeLists();
            this.toggleCollapseFollowNumberOfRows();
        }else {
            this.rows?.clear();
            this.setupDefaultAttribute();
        }
    }

    private addAttributeValue(row: UntypedFormGroup, view: AttributeRowView, attribute: DomainAttributeModel) {
        switch (attribute.dimensionUnitCode) {
            case 'CHOICE':
                this.addAttributeChoice(row, view);
                break;
            case 'NUMBER':
            case 'PERCENTAGE':
                row.get('choiceValue').setValue(view.attributeValue);
                break;
            case 'DATE':    
            case 'DATETIME':
            case 'TIME':
                row.get('choiceValue').setValue(view.attributeDateTime);
                break;
            case 'TEXT':
                row.get('choiceValue').setValue(view.attributeText);
                break;
        }
    }

    addAttributeChoice(row: UntypedFormGroup, view: AttributeRowView) {
        if (row.get('multipleChoiceFlag').value) {
            row.get('choiceValue').setValue(view?.attributeChoiceIds);
        } else {
            row.get('choiceValue').setValue(view?.attributeChoiceIds[0]);
        }
    }

    public getDomainAttrs(i) {
        if(!this.rows.controls) {
           return null;
        }
        let attributeTypeCodes = this.rows.controls.map(r => r.value.attributeType);
        let currentAttributeTypeCode = this.rows.controls[i].value.attributeType;    
        let currentDomainAttributes = this.domainAttributes$.value?.filter(x => x.attributeTypeCode == currentAttributeTypeCode);
        if (currentDomainAttributes?.length) {
            if (currentDomainAttributes[0]?.requiredFlag) {
                return currentDomainAttributes;
            }
        }
        attributeTypeCodes = attributeTypeCodes.filter(x => x != currentAttributeTypeCode);
        let domainAttrs = this.domainAttributes$.value?.filter(da => {
            return attributeTypeCodes.includes(da.attributeTypeCode) == false;
        });
        return domainAttrs;
    }
}